import { InsightCard, InsightList, Skeleton } from '@design-system';
import { formatDate } from '@utils/datetime';
import { getWordCount } from '@utils/journals/franchises';
import { readingTimeCalculator } from '@utils/readingTimeCalculator';
import { fetchStories } from '@utils/storyblok/stories';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useState } from 'react';
import { StoryData } from 'storyblok-js-client';

import {
  FranchiseStoryblok,
  InsightIdStoryblok,
} from '../../types/generated-storyblok';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';

interface Props {
  blok: InsightIdStoryblok;
  variant: 'card' | 'list';
}

export const ArticleInsight: FC<Props> = ({ blok, variant }) => {
  const { t } = useTranslation('common');
  const [emitter] = useAnalyticsEmitter();

  const [insight, setInsight] = useState<FranchiseStoryblok>();
  const [loading, setLoading] = useState(true);
  const [fullSlug, setFullSlug] = useState<string>();
  const [publishedAt, setPublishedAt] = useState<string | null>();

  const handleClick = useCallback(() => {
    emitter('', 'insight clicked', {
      'insight clicked name': insight?.title,
    });
  }, [insight, emitter]);

  useEffect(() => {
    fetchStories({
      byUuids: 'id' in blok.insight ? blok.insight.id : undefined,
      page: 1,
      perPage: 1,
    }).then(([story]) => {
      if (!story) {
        return;
      }
      setFullSlug((story as StoryData<FranchiseStoryblok>).full_slug);
      setPublishedAt((story as StoryData<FranchiseStoryblok>).published_at);
      setInsight((story as StoryData<FranchiseStoryblok>).content);
      setLoading(false);
    });
  }, [blok.insight]);

  if (!insight) {
    return null;
  }

  if (loading || !insight) {
    return variant === 'card' ? (
      <div className="flex flex-col gap-3">
        <Skeleton className="aspect-[1/1]" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-9 w-3/4 md:h-5" />
          <Skeleton className="h-9 w-1/2 md:h-5" />
        </div>
      </div>
    ) : (
      <div className="flex w-full items-center gap-3 pt-3 first:pt-0 md:pt-4">
        <Skeleton className="h-16 w-14 shrink-0" />
        <div className="flex w-full flex-col gap-1">
          <Skeleton className="h-9 w-3/4 md:h-5" />
          <Skeleton className="h-9 w-1/2 md:h-5" />
        </div>
      </div>
    );
  }

  const overline = [];

  if (publishedAt) {
    overline.push(
      formatDate(publishedAt, {
        includeYear: true,
        includeWeekday: false,
      }),
    );
  }

  const readingTime = insight.article
    ? readingTimeCalculator(getWordCount(insight.article))
    : 0;

  if (readingTime > 1) {
    overline.push(t('readingTime', { minutes: readingTime }));
  }

  if (variant === 'card') {
    return (
      <div className="relative">
        <Link
          className="group w-full overflow-hidden"
          href={`/${fullSlug}`}
          onClick={handleClick}
          prefetch={false}
        >
          <InsightCard
            image={insight.poster}
            imageRatio={insight.posterRatio || '1/1'}
            mediaDuration={insight.mediaDuration}
            overline={overline.join(' · ')}
            tag={insight.tag}
            text={insight.subtitle}
            title={insight.title}
          />
        </Link>
      </div>
    );
  }

  return (
    <div className="relative">
      <Link
        className="group w-full overflow-hidden"
        href={`/${fullSlug}`}
        onClick={handleClick}
        prefetch={false}
      >
        <InsightList
          image={insight.poster}
          overline={overline.join(' · ')}
          title={insight.title}
        />
      </Link>
    </div>
  );
};
